import React from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';

import Icon from 'presentation/icons';
import {translations} from 'presentation/translations-new';
import {MaskedInput} from 'presentation/components/MaskedInput';

import {Loading} from 'presentation/components/Loading/Loading';

import {StyledFixedFooter} from 'presentation/pages/public/styles';
import {IDENTIFIER_LENGTH, clearLoginMask, masks} from 'presentation/utils';
import {useHostName} from 'presentation/hooks';
import {useCheckValidUser} from 'presentation/pages/public/hooks/useCheckValidUser';
import {IFormData, formSchema} from './schema';

const {formLabels, formPlaceholders} = translations['pt-br'].publicFlow;
const {commons} = translations['pt-br'];

export function Step1Form() {
	const {isGndiSaoPaulo} = useHostName();

	const methods = useForm<IFormData>({
		defaultValues: {
			login: '',
		},
		resolver: zodResolver(formSchema),
		mode: 'onChange',
		delayError: 1000,
	});
	const {
		handleSubmit,
		register,
		getValues,
		formState: {errors, isSubmitting, isValid, isDirty},
	} = methods;
	const {handleCheckValidUser, showAPIError, apiErrorMsg} = useCheckValidUser({methods});

	const loginDataLength = clearLoginMask(getValues('login')).length;
	const isDisabled = !isValid || isSubmitting || loginDataLength < IDENTIFIER_LENGTH.cpf;

	const placeholder = isGndiSaoPaulo ? formPlaceholders.signInCPF : formPlaceholders.login;
	const label = isGndiSaoPaulo ? formLabels.cpf : formLabels.login;

	return (
		<FormProvider {...methods}>
			<Stack component="form" gap={2} onSubmit={handleSubmit(handleCheckValidUser)}>
				<Stack>
					<Typography variant="label" component="label" htmlFor="login">
						{label}
					</Typography>
					<MaskedInput
						{...register('login')}
						id="login"
						maskOptions={masks.register}
						autoComplete="current-login"
						error={!isValid && isDirty}
						helperText={isDirty ? errors.login?.message : undefined}
						label={placeholder}
						$fieldName="login"
					/>
				</Stack>
				{showAPIError ? <Alert severity="error">{apiErrorMsg}</Alert> : null}
				<StyledFixedFooter>
					<Button
						fullWidth
						size="large"
						disabled={isDisabled}
						type="submit"
						endIcon={
							isSubmitting ? (
								<Loading color="inherit" size={16} />
							) : (
								<Icon name="arrow-right-filled" />
							)
						}>
						{commons.keep}
					</Button>
				</StyledFixedFooter>
			</Stack>
		</FormProvider>
	);
}
